import { motion } from 'framer-motion';
import { makeStyles } from '@material-ui/core';
import LaunchIcon from '@material-ui/icons/Launch';
import { useAiTools, useToolHealth } from '../../nav/useToolHealth';

const useStyles = makeStyles({
  grid: {
    display: 'grid',
    gap: 16,
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
  },
  card: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    padding: 22,
    borderRadius: 14,
    border: '1px solid var(--q-border)',
    background: 'var(--q-bg2)',
    textDecoration: 'none',
    overflow: 'hidden',
    transition: 'border-color 200ms var(--q-ease)',
    '&:hover': { borderColor: 'rgba(16,185,129,0.45)' },
    '&:hover $launch': { opacity: 1, transform: 'translate(0,0)' },
  },
  head: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  name: {
    fontFamily: 'var(--q-font-display)',
    fontWeight: 700,
    fontSize: 17,
    letterSpacing: '-0.01em',
    color: 'var(--q-text)',
  },
  blurb: {
    color: 'var(--q-sub)',
    fontSize: 13.5,
    lineHeight: 1.55,
    margin: 0,
  },
  status: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    fontFamily: 'var(--q-font-mono)',
    fontSize: 10,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
  },
  dot: { width: 6, height: 6, borderRadius: '50%' },
  launch: {
    position: 'absolute',
    top: 18,
    right: 18,
    color: 'var(--q-emerald)',
    opacity: 0,
    transform: 'translate(-3px, 3px)',
    transition: 'opacity 200ms var(--q-ease), transform 200ms var(--q-spring)',
  },
  skeleton: {
    height: 13,
    borderRadius: 4,
    background: 'var(--q-bg3)',
    position: 'relative',
    overflow: 'hidden',
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      background:
        'linear-gradient(90deg, transparent, rgba(255,255,255,0.06), transparent)',
      animation: 'q-shimmer 1.4s infinite',
    },
  },
});

const LABEL = {
  up: 'Operational',
  down: 'Unreachable',
  unknown: 'Checking',
} as const;

const COLOR = {
  up: 'var(--q-emerald)',
  down: 'var(--q-red)',
  unknown: 'var(--q-slate)',
} as const;

export const ToolGrid = () => {
  const classes = useStyles();
  const health = useToolHealth();
  const tools = useAiTools();

  return (
    <div className={classes.grid}>
      {tools.map((tool, i) => {
        const status = health[tool.id] ?? 'unknown';
        return (
          <motion.a
            key={tool.id}
            href={tool.url}
            target="_blank"
            rel="noopener noreferrer"
            className={classes.card}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{
              delay: i * 0.08,
              duration: 0.45,
              ease: [0.16, 1, 0.3, 1],
            }}
          >
            <LaunchIcon className={classes.launch} fontSize="small" />
            <div className={classes.head}>
              <span className={classes.name}>{tool.title}</span>
            </div>
            <p className={classes.blurb}>{tool.blurb}</p>
            <span
              className={classes.status}
              style={{ color: COLOR[status], marginTop: 4 }}
            >
              <span
                className={classes.dot}
                style={{
                  background: COLOR[status],
                  boxShadow:
                    status === 'up' ? '0 0 6px var(--q-emerald)' : undefined,
                  animation:
                    status === 'up'
                      ? 'q-breathe 3s ease-in-out infinite'
                      : undefined,
                }}
              />
              {LABEL[status]}
            </span>
          </motion.a>
        );
      })}
    </div>
  );
};

/** Loading placeholder used while the catalog count resolves. */
export const LineSkeleton = ({ width = '100%' }: { width?: string }) => {
  const classes = useStyles();
  return <div className={classes.skeleton} style={{ width }} />;
};
